/**
 * Hộp "Lập đơn mua" dùng chung (05/11/2026) — Kế hoạch vật tư và Mua hàng › Hàng chờ mua.
 * - Chỉ dòng kế hoạch còn thiếu (lấy từ hàng chờ mua của máy chủ); mỗi vật tư 1 dòng gộp các LSX.
 * - 1 đơn mua / NCC; vật tư chưa có NCC chọn NCC + "Lưu làm NCC mặc định" (mặc định tích).
 * - SL mặc định = số còn thiếu; nhập theo ĐVT chính hoặc ĐVT phụ (Ram / Tấn); mua dư thì phần dư nhập tồn chung.
 * - Máy chủ khoá kế hoạch, chia SL cho từng kế hoạch theo ngày cần (purchaseOrders.createFromPlans).
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShoppingCart, Loader2, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatDualQty, toPrimaryQty, toPrimaryUnitPrice } from "@apv-erp/status";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NumericInput } from "@/components/ui/numeric-input";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { DualUnitQtyInput } from "@/components/shared/DualUnitQtyInput";
import { EntityLink } from "@/components/shared/EntityLink";
import { trpcClient } from "@/lib/trpc";
import { extractErrorMessage, formatMoney } from "@/lib/utils";

interface ShortageRow {
  materialPlanId: string;
  productionOrderId?: string | null;
  poCode?: string;
  customerName?: string;
  materialId: string;
  materialCode?: string;
  materialName?: string;
  unit?: string;
  materialUnit?: string | null;
  secondaryUnit?: string | null;
  secondaryFactor?: number | null;
  shortageQty: number;
  unitPrice?: number;
  vendorId?: string | null;
  vendorName?: string;
}

interface MaterialLine {
  materialId: string;
  code: string;
  name: string;
  unitMeta: { unit: string; secondaryUnit?: string | null; secondaryFactor?: number | null };
  plans: ShortageRow[];
  shortage: number;
  vendorId: string;
  vendorName?: string;
  hadVendor: boolean;
  saveAsDefaultVendor: boolean;
  qty: number;
  entryUnit: string | null;
  price: number;
  expanded: boolean;
}

export interface CreatePurchaseOrdersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dòng kế hoạch đã chọn; dòng không còn thiếu tự bỏ */
  planIds: string[];
  onSuccess?: () => void;
}

export function CreatePurchaseOrdersDialog({ open, onOpenChange, planIds, onSuccess }: CreatePurchaseOrdersDialogProps) {
  const [lines, setLines] = useState<MaterialLine[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<any[] | null>(null);
  const key = planIds.join(",");

  const { data, isLoading } = useQuery({
    queryKey: ["purchase-orders", "shortage-for-dialog", key],
    queryFn: () => trpcClient.purchaseOrders.getMrpShortageQueue.query({} as any),
    enabled: open && planIds.length > 0,
    staleTime: 0,
  });

  // Gộp các kế hoạch còn thiếu theo vật tư
  useEffect(() => {
    if (!open) {
      setCreated(null);
      return;
    }
    const wanted = new Set(planIds);
    const rows: ShortageRow[] = ((data as any)?.items || []).filter((r: ShortageRow) => wanted.has(r.materialPlanId) && r.shortageQty > 0);
    const byMaterial = new Map<string, MaterialLine>();
    for (const r of rows) {
      const cur = byMaterial.get(r.materialId);
      if (cur) {
        cur.plans.push(r);
        cur.shortage += r.shortageQty;
        cur.qty += r.shortageQty;
        continue;
      }
      byMaterial.set(r.materialId, {
        materialId: r.materialId,
        code: r.materialCode || "",
        name: r.materialName || "",
        unitMeta: { unit: r.materialUnit || r.unit || "", secondaryUnit: r.secondaryUnit, secondaryFactor: r.secondaryFactor },
        plans: [r],
        shortage: r.shortageQty,
        vendorId: r.vendorId || "",
        vendorName: r.vendorId ? r.vendorName : undefined,
        hadVendor: !!r.vendorId,
        saveAsDefaultVendor: !r.vendorId,
        qty: r.shortageQty,
        entryUnit: null,
        price: Number(r.unitPrice || 0),
        expanded: false,
      });
    }
    setLines([...byMaterial.values()]);
  }, [open, data, key]);

  const update = (i: number, patch: Partial<MaterialLine>) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const primaryQty = (l: MaterialLine) => (l.entryUnit ? toPrimaryQty(l.qty, l.entryUnit, l.unitMeta) ?? l.qty : l.qty);
  const vendorGroups = useMemo(() => {
    const m = new Map<string, MaterialLine[]>();
    for (const l of lines) if (l.vendorId) m.set(l.vendorId, [...(m.get(l.vendorId) || []), l]);
    return m;
  }, [lines]);
  const missingVendor = lines.filter((l) => !l.vendorId).length;
  const invalidQty = lines.some((l) => !(l.qty > 0));
  const total = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.price) || 0), 0);

  const submit = async () => {
    setSubmitting(true);
    try {
      const orders = [...vendorGroups.entries()].map(([vendorId, ls]) => ({
        vendorId,
        lines: ls.map((l) => ({
          materialId: l.materialId,
          planIds: l.plans.map((p) => p.materialPlanId),
          ...(l.entryUnit
            ? { entryUnit: l.entryUnit, entryQty: l.qty, entryUnitPrice: l.price }
            : { quantity: l.qty, unitPrice: l.price }),
          saveAsDefaultVendor: l.saveAsDefaultVendor,
        })),
      }));
      const res: any = await trpcClient.purchaseOrders.createFromPlans.mutate({ orders } as any);
      setCreated(res.orders || []);
      toast.success(`Đã lập ${res.orders?.length || 0} đơn mua`);
      onSuccess?.();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Không lập được đơn mua");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-5xl max-h-[90vh] flex flex-col" data-testid="create-po-dialog">
        <DialogHeader icon={ShoppingCart}>
          <DialogTitle>Lập đơn mua</DialogTitle>
          <DialogDescription>
            Mỗi vật tư 1 dòng (gộp các LSX), mỗi nhà cung cấp 1 đơn mua; số lượng mặc định là phần còn thiếu.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3">
          {created ? (
            <div className="py-4 space-y-3" data-testid="create-po-result">
              <div className="flex items-center gap-2 text-emerald-700 font-medium">
                <CheckCircle2 className="h-5 w-5" /> Đã lập {created.length} đơn mua (Chờ duyệt)
              </div>
              <ul className="divide-y rounded-lg border">
                {created.map((po) => (
                  <li key={po.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <EntityLink entityType="purchaseOrder" id={po.id} code={po.poId} />
                    <span className="flex-1 break-words whitespace-normal text-slate-600">{po.vendorName}</span>
                    <span className="text-slate-500">{po.materials} vật tư</span>
                    <span className="font-mono tabular-nums">{formatMoney(po.totalAmount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Đang tải số còn thiếu...
            </div>
          ) : lines.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="create-po-empty">
              Các dòng đã chọn không còn số cần mua (đã đặt đủ hoặc đủ tồn).
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="text-left px-2 py-2">Vật tư</th>
                  <th className="text-right px-2 py-2 whitespace-nowrap">Còn thiếu</th>
                  <th className="text-right px-2 py-2 w-[190px]">SL đặt</th>
                  <th className="text-right px-2 py-2 w-[130px]">Đơn giá / ĐVT nhập</th>
                  <th className="text-left px-2 py-2 w-[240px]">Nhà cung cấp</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lines.map((l, i) => {
                  const over = primaryQty(l) > l.shortage + 1e-9;
                  return (
                    <tr key={l.materialId} className="align-top" data-testid="create-po-line">
                      <td className="px-2 py-2">
                        <div className="font-medium text-slate-800">{l.code} — {l.name}</div>
                        <button
                          type="button"
                          className="mt-0.5 inline-flex items-center gap-1 text-xs text-indigo-600"
                          onClick={() => update(i, { expanded: !l.expanded })}
                        >
                          {l.expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          {l.plans.length} LSX
                        </button>
                        {l.expanded && (
                          <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                            {l.plans.map((p) => (
                              <li key={p.materialPlanId} className="flex gap-2">
                                {p.productionOrderId ? (
                                  <EntityLink entityType="productionOrder" id={p.productionOrderId} code={p.poCode} />
                                ) : (
                                  <span>{p.poCode || "—"}</span>
                                )}
                                <span className="break-words whitespace-normal">{p.customerName}</span>
                                <span className="ml-auto tabular-nums">{formatDualQty(p.shortageQty, l.unitMeta)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{formatDualQty(l.shortage, l.unitMeta)}</td>
                      <td className="px-2 py-2">
                        <DualUnitQtyInput
                          testId={`create-po-qty-${i}`}
                          value={l.qty}
                          entryUnit={l.entryUnit}
                          material={l.unitMeta}
                          onChange={(qty, unit) => {
                            let price = l.price;
                            if ((unit || null) !== (l.entryUnit || null) && price) {
                              const f = Number(l.unitMeta.secondaryFactor) || 1;
                              price = Math.round((unit ? price * f : price / f) * 100) / 100;
                            }
                            update(i, { qty, entryUnit: unit, price });
                          }}
                        />
                        {over && (
                          <div className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-amber-700">
                            <AlertTriangle className="h-3 w-3" /> Mua dư — phần dư nhập tồn chung
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <NumericInput
                          thousandSeparator="."
                          decimalSeparator=","
                          className="h-9 text-right"
                          value={l.price}
                          onValueChange={(v) => update(i, { price: v.floatValue ?? 0 })}
                        />
                        {l.entryUnit && (
                          <div className="mt-0.5 text-right text-[11px] text-slate-500">
                            {formatMoney(toPrimaryUnitPrice(l.price, l.entryUnit, l.unitMeta) ?? 0)} / {l.unitMeta.unit}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 space-y-1">
                        <SharedCombobox
                          type="vendor"
                          value={l.vendorId}
                          valueLabel={l.vendorName}
                          onChange={(id, opt) => update(i, { vendorId: id || "", vendorName: opt?.label })}
                          placeholder="Chọn NCC"
                          error={!l.vendorId}
                        />
                        {l.vendorId && (
                          <label className="flex items-center gap-1.5 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5"
                              checked={l.saveAsDefaultVendor}
                              onChange={(e) => update(i, { saveAsDefaultVendor: e.target.checked })}
                              data-testid={`create-po-save-vendor-${i}`}
                            />
                            Lưu làm NCC mặc định{l.hadVendor ? "" : " (vật tư chưa có NCC)"}
                          </label>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {!created && lines.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t pt-3 text-xs text-slate-600">
            <span>Sẽ lập</span>
            {[...vendorGroups.entries()].map(([vid, ls]) => (
              <Badge key={vid} variant="outline">
                {ls[0].vendorName || "NCC"} · {ls.length} vật tư
              </Badge>
            ))}
            {missingVendor > 0 && <Badge className="bg-amber-100 text-amber-800">{missingVendor} vật tư chưa chọn NCC</Badge>}
            <span className="ml-auto font-medium text-slate-800">Tạm tính {formatMoney(total)}</span>
          </div>
        )}

        <DialogFooter>
          {created ? (
            <Button size="sm" onClick={() => onOpenChange(false)}>Đóng</Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Huỷ</Button>
              <Button
                size="sm"
                data-testid="create-po-submit"
                disabled={submitting || lines.length === 0 || missingVendor > 0 || invalidQty}
                onClick={submit}
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Lập {vendorGroups.size} đơn mua
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
